/**
 * Payment Proof, on Delivery Details.
 *
 * The screenshot a counter attached when it recorded a transfer as Paid. It
 * was always stored and linked; this is the part of the screen that shows it.
 *
 * Three things it is careful about:
 *
 *   **The original, not a copy.** The thumbnail, the full view and the download
 *   are all the same bytes the API serves — checked against the checksum
 *   recorded at upload — so what an owner looks at is what was uploaded.
 *
 *   **Fetched, not linked.** An <img src> cannot carry the Authorization header,
 *   so the file is fetched with the session and shown from an object URL. The
 *   API decides who may see it (`deliveries:read`, the delivery's location),
 *   exactly as it decides who may see the delivery.
 *
 *   **Missing is said, not hidden.** A delivery that needs proof and has none,
 *   and a proof whose file storage cannot produce, are different problems with
 *   different fixes, and each gets its own sentence.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PROOF_EXEMPT_METHODS, type Delivery, type DeliveryProofFile } from '@cenox/shared';
import { ApiError, downloadFile, fetchBlob } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';
import { date } from '../../lib/format';
import { Button, Modal } from '../../components/ui';

/** Formats a browser can draw in an <img>. HEIC is stored faithfully but not drawable. */
const DRAWABLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

function size(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Why a proof could not be shown, in words the reader can act on. */
function explain(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404 && /attachment file/i.test(error.message)) {
      return 'The proof is recorded on this delivery, but its file is missing from storage. Nothing has been changed; ask an owner to check the stored files.';
    }
    if (error.status === 404) return 'This proof of payment could not be found.';
    if (error.status === 403) return 'You do not have permission to view this proof of payment.';
  }
  return 'The proof of payment could not be loaded. Try again in a moment.';
}

function ProofFile({
  delivery,
  file,
  timezone,
  label,
}: {
  delivery: Delivery;
  file: DeliveryProofFile;
  timezone: string;
  label: string;
}): ReactNode {
  const path = useBusinessPath();
  const [viewing, setViewing] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const isCurrent = delivery.paymentProofAttachmentId === file.attachmentId;
  const query = isCurrent ? '' : `?attachmentId=${file.attachmentId}`;
  const source = path(`/deliveries/${delivery.id}/payment-proof${query}`);

  const blob = useQuery({
    queryKey: ['delivery-proof', delivery.id, file.attachmentId],
    queryFn: ({ signal }) => fetchBlob(source, signal),
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Created and revoked in one effect, so a re-run never leaves a revoked URL on screen.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) return undefined;
    const next = URL.createObjectURL(blob.data);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setUrl(null);
    };
  }, [blob.data]);

  const drawable = DRAWABLE.has(file.mimeType);

  const download = async (): Promise<void> => {
    setDownloadError(null);
    try {
      await downloadFile(`${source}${query ? '&' : '?'}download=1`, file.filename);
    } catch (error) {
      setDownloadError(explain(error));
    }
  };

  return (
    <div className="flex flex-wrap items-start gap-4" data-testid="proof-file">
      <button
        type="button"
        onClick={() => setViewing(true)}
        disabled={!url}
        aria-label={`View ${label.toLowerCase()} full size`}
        className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 disabled:cursor-default"
      >
        {blob.isPending ? (
          <span className="text-xs text-slate-400">Loading…</span>
        ) : url && drawable ? (
          <img src={url} alt={`${label}: ${file.filename}`} className="h-full w-full object-cover" />
        ) : url ? (
          <span className="px-2 text-center text-xs text-slate-600">
            {file.mimeType === 'application/pdf' ? 'PDF' : file.mimeType.split('/')[1]?.toUpperCase()}
          </span>
        ) : (
          <span className="px-2 text-center text-xs text-red-600">Unavailable</span>
        )}
      </button>

      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        <p className="truncate text-sm text-slate-900">{file.filename}</p>
        <p className="text-xs text-slate-500">
          {size(file.byteSize)} · uploaded {date(file.uploadedAt, timezone)}
          {file.uploadedByName ? ` by ${file.uploadedByName}` : ''}
        </p>
        {blob.isError && (
          <p role="alert" className="text-xs text-red-600">
            {explain(blob.error)}
          </p>
        )}
        {downloadError && (
          <p role="alert" className="text-xs text-red-600">
            {downloadError}
          </p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            variant="secondary"
            disabled={!url}
            onClick={() => {
              // A file the page cannot draw opens in the browser's own viewer.
              if (url && !drawable) window.open(url, '_blank', 'noopener');
              else setViewing(true);
            }}
          >
            View Full Image
          </Button>
          <Button variant="ghost" disabled={blob.isError} onClick={() => void download()}>
            Download
          </Button>
        </div>
      </div>

      <Modal open={viewing && Boolean(url) && drawable} onClose={() => setViewing(false)} title={file.filename} size="lg">
        {url && (
          <img
            src={url}
            alt={`${label}: ${file.filename}, full size`}
            className="mx-auto h-auto max-w-full"
          />
        )}
      </Modal>
    </div>
  );
}

export function PaymentProofSection({
  delivery,
  timezone,
}: {
  delivery: Delivery;
  timezone: string;
}): ReactNode {
  const proof = delivery.paymentProof;
  const current = proof?.current ?? null;
  const earlier = proof?.earlier ?? [];

  const paid = (delivery.paymentStatus ?? '').trim().toLowerCase() === 'paid';
  const needsProof = paid && !PROOF_EXEMPT_METHODS.includes((delivery.paymentMethod ?? '').trim());

  // Nothing to show and nothing missing: cash, or not paid yet.
  if (!current && earlier.length === 0 && !needsProof) return null;

  return (
    <section aria-labelledby="payment-proof-heading" className="border-y border-slate-100 px-4 py-4">
      <h2 id="payment-proof-heading" className="mb-3 text-sm font-medium text-slate-900">
        Payment Proof
      </h2>

      {current ? (
        <ProofFile delivery={delivery} file={current} timezone={timezone} label="Payment screenshot" />
      ) : delivery.paymentProofAttachmentId ? (
        <p role="alert" className="text-sm text-red-700">
          This delivery names a proof of payment, but its file record could not be found. Nothing has
          been changed; ask an owner to check it.
        </p>
      ) : (
        <p className="text-sm text-slate-600">
          No proof of payment is on file for this {delivery.paymentMethod ?? 'payment'}.
          {' '}Records made before proof was required, or imported from another system, may not have one.
        </p>
      )}

      {earlier.length > 0 && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-500">
            Replaced by a newer upload, and kept with this delivery as evidence.
          </p>
          {earlier.map((file) => (
            <ProofFile key={file.attachmentId} delivery={delivery} file={file} timezone={timezone} label="Earlier proof" />
          ))}
        </div>
      )}
    </section>
  );
}
